/**
 * Agente: elenco + creazione richiesta campioni.
 */
import React, { useEffect, useState } from 'react';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import AgentLayout from './AgentLayout';
import { Package, Plus, X, Trash2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: { label: 'Da approvare', color: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approvato', color: 'bg-blue-100 text-blue-800' },
  in_preparation: { label: 'In preparazione', color: 'bg-teal-100 text-teal-800' },
  shipped: { label: 'Spedito', color: 'bg-purple-100 text-purple-800' },
  delivered: { label: 'Consegnato', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rifiutato', color: 'bg-red-50 text-red-700' },
  cancelled: { label: 'Annullato', color: 'bg-gray-100 text-gray-600' },
};

const emptyForm = {
  customer_id: '',
  recipient_name: '',
  recipient_phone: '',
  recipient_email: '',
  shipping_address: '',
  shipping_city: '',
  shipping_province: '',
  shipping_zipcode: '',
  shipping_notes: '',
  reason: '',
  // I campioni sono sempre in formato 20cl (bottiglietta standard)
  items: [{ liquor_id: '', liquor_name: '', format: '20cl', quantity: 3 }],
};

const AgentSamples = () => {
  const { agentToken } = useAgentAuth();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState({ liquors: [], formats: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const auth = () => ({ Authorization: `Bearer ${agentToken}` });

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, cRes, catRes] = await Promise.all([
        fetch(`${API}/api/agent/sample-orders`, { headers: auth() }),
        fetch(`${API}/api/agent/customers`, { headers: auth() }),
        fetch(`${API}/api/agent/catalog`, { headers: auth() }),
      ]);
      if (sRes.ok) setSamples((await sRes.json()).data || []);
      if (cRes.ok) setCustomers((await cRes.json()).data || []);
      if (catRes.ok) {
        const j = await catRes.json();
        setCatalog({ liquors: j.liquors || [], formats: j.formats || [] });
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (agentToken) load(); /* eslint-disable-next-line */ }, [agentToken]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    if (c) {
      setForm((f) => ({
        ...f,
        customer_id: id,
        recipient_name: c.companyName || `${c.firstName} ${c.lastName}`,
        recipient_email: c.email,
        recipient_phone: c.phone || '',
      }));
    } else {
      update('customer_id', id);
    }
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { liquor_id: '', liquor_name: '', format: '5cl', quantity: 3 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, patch) => setForm((f) => ({
    ...f,
    items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.items.length === 0) { setError('Aggiungi almeno un campione'); return; }
    if (form.items.some((it) => !it.liquor_name)) { setError('Seleziona un liquore per ogni riga'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, customer_id: form.customer_id || null };
      const res = await fetch(`${API}/api/agent/sample-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (res.ok) {
        setShowNew(false);
        setForm(emptyForm);
        load();
      } else {
        setError(j.detail || 'Errore invio richiesta');
      }
    } finally { setSaving(false); }
  };

  const totalBottles = form.items.reduce((s, i) => s + Number(i.quantity || 0), 0);

  return (
    <AgentLayout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Campioni</h1>
          <p className="text-sm text-gray-600 mt-1">{samples.length} richieste totali. Richiedi campioni per potenziali clienti o degustazioni.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="btn-new-sample"
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium">
          <Plus className="w-4 h-4" /> Nuova richiesta
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">Caricamento…</div>
        ) : samples.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            <Package className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            Nessuna richiesta. Clicca "Nuova richiesta" per iniziare.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-purple-50/50 text-purple-700 uppercase text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Destinatario</th>
                <th className="text-left px-4 py-3">Città</th>
                <th className="text-right px-4 py-3">Bottiglie</th>
                <th className="text-center px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3">Tracking</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {samples.map((s) => {
                const st = STATUS_LABELS[s.status] || { label: s.status, color: 'bg-gray-100' };
                return (
                  <tr key={s.id} className="hover:bg-purple-50/40" data-testid={`sample-row-${s.id}`}>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{s.recipient_name}</div>
                      {s.customer?.company && <div className="text-[10px] text-purple-600">🏢 {s.customer.company}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{s.shipping_city}</td>
                    <td className="px-4 py-3 text-right font-mono">{s.total_bottles}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.color}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{s.tracking_number || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* New sample modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-sample-modal">
          <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-semibold">Nuova richiesta campioni</h2>
              <button type="button" onClick={() => setShowNew(false)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <div>
                <label className="text-xs font-semibold uppercase text-gray-700">Cliente destinatario (opzionale)</label>
                <select value={form.customer_id} onChange={(e) => setCustomer(e.target.value)}
                  data-testid="sample-customer-select"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                  <option value="">— Uso interno / demo (non collegato) —</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.companyName || `${c.firstName} ${c.lastName}`} · {c.email}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-700">Destinatario *</label>
                  <input required value={form.recipient_name} onChange={(e) => update('recipient_name', e.target.value)}
                    data-testid="input-recipient" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-700">Telefono</label>
                  <input value={form.recipient_phone} onChange={(e) => update('recipient_phone', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold uppercase text-gray-700">Email</label>
                  <input type="email" value={form.recipient_email} onChange={(e) => update('recipient_email', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-semibold uppercase text-gray-700">Indirizzo spedizione *</label>
                  <input required value={form.shipping_address} onChange={(e) => update('shipping_address', e.target.value)}
                    data-testid="input-shipping-address" placeholder="Via, numero civico"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-700">Città *</label>
                  <input required value={form.shipping_city} onChange={(e) => update('shipping_city', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-700">Provincia</label>
                  <input value={form.shipping_province} onChange={(e) => update('shipping_province', e.target.value)}
                    maxLength="2" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm uppercase" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-700">CAP</label>
                  <input value={form.shipping_zipcode} onChange={(e) => update('shipping_zipcode', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold uppercase text-gray-700">Note spedizione</label>
                  <input value={form.shipping_notes} onChange={(e) => update('shipping_notes', e.target.value)}
                    placeholder="Orari, citofoni, referenti…"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase text-gray-700">
                    Campioni richiesti <span className="text-purple-600">· formato fisso 20cl</span>
                  </label>
                  <button type="button" onClick={addItem}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                    <Plus className="w-3 h-3" /> Aggiungi
                  </button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-purple-50/40 border border-purple-100 rounded-lg p-2">
                      <select value={it.liquor_id} onChange={(e) => {
                        const lq = catalog.liquors.find((l) => l.id === e.target.value);
                        updateItem(i, { liquor_id: e.target.value, liquor_name: lq?.name || '' });
                      }}
                        className="col-span-7 px-2 py-1.5 border border-gray-300 rounded text-xs"
                        data-testid={`sample-item-liquor-${i}`}>
                        <option value="">Seleziona liquore</option>
                        {catalog.liquors.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                      <div className="col-span-2 text-center text-xs font-semibold text-purple-700 bg-white border border-purple-200 rounded py-1.5">
                        20cl
                      </div>
                      <input type="number" min="1" max="50" value={it.quantity}
                        onChange={(e) => updateItem(i, { quantity: Number(e.target.value) })}
                        className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-xs text-center"
                        data-testid={`sample-item-qty-${i}`} />
                      <button type="button" onClick={() => removeItem(i)}
                        className="col-span-1 p-1.5 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-xs text-gray-600">Totale bottiglie richieste: <b className="font-mono">{totalBottles}</b> × 20cl</div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-gray-700">Motivo / contesto</label>
                <textarea rows={2} value={form.reason} onChange={(e) => update('reason', e.target.value)}
                  placeholder="Es. Degustazione ristorante, presentazione a fiera, cliente in trattativa 500 pz…"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>

              {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">Annulla</button>
              <button type="submit" disabled={saving} data-testid="btn-submit-sample"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Invia richiesta
              </button>
            </div>
          </form>
        </div>
      )}
    </AgentLayout>
  );
};

export default AgentSamples;
